import { bindable, computedFrom, autoinject, bindingMode } from 'aurelia-framework';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { DOM } from 'aurelia-pal';
import { Router } from 'aurelia-router';
import he from 'he';
import numeral from 'numeral';
import { State } from '../../services/global-state';
import { PayrazrService } from '../../services/payrazr';
import { ValidationHelpers } from '../../services/validation-helpers';
import { CardFormData } from './card-form/card-form';
import { CheckFormData } from './check-form/check-form';
import { WalletForm } from './wallet-form/wallet-form';
import { I18N } from 'aurelia-i18n';
import { Customer, Account, Payment } from '../../models/customer';
import * as moment from 'moment';
import 'moment-timezone';
import { FeeCalculationType } from '../../models/feeCalculationType';
import { HpfPaymentMethodService } from '../../services/hpf-payment-method.service';
import { CheckPaymentMethod } from '../../services/hpf-transaction-service';
import { HtmlHelper } from '../../helpers/html-helper';
import { FeeCalculationValue } from '../../models/feeCalculationValue';
import { PaymentPlanType } from '../../models/paymentPlanType';
import { ApplePaySettings, DigitalWalletType } from '../../models/digital-wallet';

@autoinject
export class PaymentFormCustomElement {
	@bindable({ defaultBindingMode: bindingMode.fromView }) fee; // Used to output calculated $ amount
	@bindable({ defaultBindingMode: bindingMode.fromView }) feeValue; // Used to output calculated $ or % amount
	@bindable({ defaultBindingMode: bindingMode.fromView }) feeCalculationType; // Used to output calculated $ or % type
	@bindable({ defaultBindingMode: bindingMode.fromView }) totalAmount; // Used to output calculated amount
	@bindable customer: Customer;
	@bindable balance: any;
	@bindable amount: any;
	@bindable initializeAmountAsBalance: any = true;
	@bindable showCvv: any;
	@bindable showFirstCol: any = true;
	@bindable showAmount: any;
	@bindable hideFeeAndTotal: any;
	@bindable showDate: any;
	@bindable showButton: any;
	@bindable showBackButton: any;
	@bindable paymentInformation: any;
	@bindable disableEdit: any;
	@bindable notShowEcheck: any;
	@bindable isRehabPaymentPlanForm: any;
	@bindable showTitle: boolean = true;
	@bindable noMethodSelected: any;
	@bindable disableAmountValidation: any = false;
	@bindable showInstallmentsDeclinedDisclosure: any = false;
	@bindable showInstallmentsRetryDisclosure: any = false;
	@bindable numberOfRetries: any = 0;
	@bindable showMinMaxPaymentAmounts: any = false;
	@bindable disableFormFields: any = false;
	@bindable isInstallment: any = false;
	@bindable numberOfServiceFee: any = 1;
	@bindable showConvenienceFeeDisclosureOnPaymentForm: any = true;
	@bindable accountMinPayment: any;
	@bindable showRecurringBillingDeclinedOrReturnedDisclosure: any = false;
	@bindable accounts: Array<Account>;
	@bindable resetAccountInfo: any = false;
	@bindable date: any;
	@bindable dateMinFutureStartDateDays: number;
	@bindable dateMaxFutureStartDateDays: number;
	@bindable({ defaultBindingMode: bindingMode.twoWay }) selectedWalletToCustomer: Customer;
	@bindable displaySelectedWalletToCustomer: boolean = true;
	@bindable feeValues: FeeCalculationValue[];
	@bindable paymentPlanType: PaymentPlanType = PaymentPlanType.NotSet; // used to dynamically show tiered fees table for autopays.
	@bindable formattedConvenienceFee: string;
	@bindable isFeeOverridden: boolean;
	@bindable isTieredFees: boolean;
	@bindable paymentMethodKey: number;
	@bindable allowSingleFuture: boolean;
	@bindable isDigitalWallet: boolean;
	@bindable forceEmailRequired: boolean = false;
	// Not allowed on ezpay and SLR
	@bindable showAddPaymentMethodToWallet: any = true;
	@bindable merchantKey: number;

	model: Model;
	showResetButton: any;
	resetButtonLabel: any;
	disableField: any;
	showConvenienceFees: any;
	states: any;
	enforceMinPayment: any;
	isEzpay: any;
	paymentMethods: any;
	maxIsBalanceDue: any;
	paymentAmountSettings: any;
	dateSettings: any;
	zipcodeSettings: any;
	showCheckNumber: any;
	minimumPaymentAmount: any;
	maximumPaymentAmount: any;
	cardNumber: any;
	cardInfoKey: any;
	portfolioSettings: any;
	portfolioName: string;
	cachedCombinedAccountsBalance: number;
	ezPayMerchantSelectedChanged: Subscription;

	constructor(
		private element: Element,
		public globalState: State,
		private payrazrService: PayrazrService,
		private router: Router,
		private i18n: I18N,
		private hpfPaymentMethodService: HpfPaymentMethodService,
		private eventAggregator: EventAggregator
	) {
		this.model = new Model(globalState, payrazrService, this.i18n);
		this.showResetButton = globalState.business.portalSettings.showResetButton;
		this.resetButtonLabel = globalState.business.portalSettings.resetButtonLabel || this.i18n.i18next.t('reset', 'Reset');
		this.showCvv = this.model.cardData.showCvv;
		this.enforceMinPayment = this.globalState.business.portalSettings.requireMinPayment;
		this.disableField = false;
		this.showConvenienceFees = false;
		this.minimumPaymentAmount = numeral(globalState.business.portalSettings.minimumPaymentAmount).format('$0,0.00');
		this.maximumPaymentAmount = numeral(globalState.business.portalSettings.maximumPaymentAmount).format('$0,0.00');
		this.isEzpay = this.isEzpayForm();

		this.states = [
			{ id: '', name: 'Select State...' }
			// ,{id:'AB',name:'Alberta'}
			, { id: 'AK', name: 'Alaska' }
			, { id: 'AL', name: 'Alabama' }
			, { id: 'AR', name: 'Arkansas' }
			, { id: 'AZ', name: 'Arizona' }
			// ,{id:'BC',name:'British Columbia'}
			, { id: 'CA', name: 'California' }
			, { id: 'CO', name: 'Colorado' }
			, { id: 'CT', name: 'Connecticut' }
			, { id: 'DC', name: 'Washington DC' }
			, { id: 'DE', name: 'Delaware' }
			, { id: 'FL', name: 'Florida' }
			, { id: 'GA', name: 'Georgia' }
			, { id: 'HI', name: 'Hawaii' }
			, { id: 'IA', name: 'Iowa' }
			, { id: 'ID', name: 'Idaho' }
			, { id: 'IL', name: 'Illinois' }
			, { id: 'IN', name: 'Indiana' }
			, { id: 'KS', name: 'Kansas' }
			, { id: 'KY', name: 'Kentucky' }
			, { id: 'LA', name: 'Louisiana' }
			, { id: 'MA', name: 'Massachusetts' }
			, { id: 'MB', name: 'Manitoba' }
			, { id: 'MD', name: 'Maryland' }
			, { id: 'ME', name: 'Maine' }
			, { id: 'MI', name: 'Michigan' }
			, { id: 'MN', name: 'Minnesota' }
			, { id: 'MO', name: 'Missouri' }
			, { id: 'MS', name: 'Mississippi' }
			, { id: 'MT', name: 'Montana' }
			// ,{id:'NB',name:'New Brunswick'}
			, { id: 'NC', name: 'North Carolina' }
			, { id: 'ND', name: 'North Dakota' }
			, { id: 'NE', name: 'Nebraska' }
			, { id: 'NH', name: 'New Hampshire' }
			, { id: 'NJ', name: 'New Jersey' }
			// ,{id:'NL',name:'Newfoundland and Labrador'}
			, { id: 'NM', name: 'New Mexico' }
			// ,{id:'NS',name:'Nova Scotia'}
			// ,{id:'NT',name:'Northwest Territories'}
			// ,{id:'NU',name:'Nunavut'}
			, { id: 'NV', name: 'Nevada' }
			, { id: 'NY', name: 'New York' }
			, { id: 'OH', name: 'Ohio' }
			, { id: 'OK', name: 'Oklahoma' }
			// ,{id:'ON',name:'Ontario'}
			, { id: 'OR', name: 'Oregon' }
			, { id: 'PA', name: 'Pennsylvania' }
			// ,{id:'PE',name:'Prince Edward Island'}
			, { id: 'PR', name: 'Puerto Rico' }
			// ,{id:'QC',name:'Quebec'}
			, { id: 'RI', name: 'Rhode Island' }
			, { id: 'SC', name: 'South Carolina' }
			, { id: 'SD', name: 'South Dakota' }
			// ,{id:'SK',name:'Saskatchewan'}
			, { id: 'TN', name: 'Tennessee' }
			, { id: 'TX', name: 'Texas' }
			, { id: 'UT', name: 'Utah' }
			, { id: 'VA', name: 'Virginia' }
			, { id: 'VT', name: 'Vermont' }
			, { id: 'WA', name: 'Washington' }
			, { id: 'WI', name: 'Wisconsin' }
			, { id: 'WV', name: 'West Virginia' }
			, { id: 'WY', name: 'Wyoming' }
			// ,{id:'YT',name:'Yukon'}
		];
	}

	applyQueryStringParams() {
		if (!this.payrazrService) {
			return;
		}

		try {
			if (this.payrazrService.getParameterValue('locked')) {
				this.disableField = true;
			}

			if (this.payrazrService.getParameterValue('paymentDescription')) {
				this.model.description = this.payrazrService.getParameterValue('paymentDescription').substr(0, 80);
			}

			if (this.payrazrService.getParameterValue('paymentAmount')) {
				this.model.amount = this.payrazrService.getParameterValue('paymentAmount').substr(0, 12);
			}

			if (this.payrazrService.getParameterValue('billingAddress')) {
				this.model.billingAddress = this.payrazrService.getParameterValue('billingAddress').substr(0, 50);
			}

			if (this.payrazrService.getParameterValue('customerName')) {
				this.model.accountName = this.payrazrService.getParameterValue('customerName').substr(0, 100);
			}

			if (this.payrazrService.getParameterValue('zipCode')) {
				this.model.accountZip = this.payrazrService.getParameterValue('zipCode').substr(0, 9);
			}

			if (this.payrazrService.getParameterValue('billingCity')) {
				this.model.billingCity = this.payrazrService.getParameterValue('billingCity').substr(0, 25);
			}

			if (this.payrazrService.getParameterValue('billingState')) {
				this.model.billingState = this.payrazrService.getParameterValue('billingState').substr(0, 5).toUpperCase();
			}

			// BT Webpay Supported Fields
			if (this.payrazrService.getParameterValue('UMtransAmt')) {
				this.model.amount = this.payrazrService.getParameterValue('UMtransAmt').substr(0, 12);
			}

			if (this.payrazrService.getParameterValue('UMdescription')) {
				this.model.description = this.payrazrService.getParameterValue('UMdescription').substr(0, 80);
			}

			if (this.payrazrService.getParameterValue('UMName')) {
				this.model.accountName = this.payrazrService.getParameterValue('UMName').substr(0, 100);
			}

			if (this.payrazrService.getParameterValue('UMrouting')) {
				this.model.checkData.transitNumber = this.payrazrService.getParameterValue('UMrouting');
			}

			if (this.payrazrService.getParameterValue('UMphonenumber')) {
				this.model.billingPhone = this.payrazrService.getParameterValue('UMphonenumber');
			}

			if (this.payrazrService.getParameterValue('UMemail')) {
				this.model.billingEmail = this.payrazrService.getParameterValue('UMemail');
			}

			if (this.payrazrService.getParameterValue('UMfnameCC')) {
				if (this.payrazrService.getParameterValue('UMlnameCC')) {
					this.model.accountName = this.payrazrService.getParameterValue('UMfnameCC') + ' ' + this.payrazrService.getParameterValue('UMlnameCC');
				} else {
					this.model.accountName = this.payrazrService.getParameterValue('UMfnameCC');
				}
			}

			if (this.payrazrService.getParameterValue('UMstreet')) {
				this.model.billingAddress = this.payrazrService.getParameterValue('UMstreet');
			}

			if (this.payrazrService.getParameterValue('UMzip')) {
				this.model.accountZip = this.payrazrService.getParameterValue('UMzip');
			}
		}
		catch (err) {
			console.error('Error encountered in querystring params.');
		}

		this.calculateTotalAmount();
	}

	applyPortfolioSettingsToPortal() {
		this.portfolioName = this.accounts[0].portfolioName; // Use the first account for portfolio name

		// Loop through accounts and get the most restrictive settings
		this.accounts.forEach(account => {
			let accountPortfolio = this.globalState.business.fetchPortfolioSettings(account.portfolioName);
			if (!this.portfolioSettings) {
				this.portfolioSettings = accountPortfolio;
			} else {
				if (!accountPortfolio.settings.paymentMethods.allowCreditCard) {
					this.portfolioSettings.settings.paymentMethods.allowCreditCard = false;
					this.portfolioSettings.paymentMethods.allowCreditCard = false;
				}

				if (!accountPortfolio.settings.paymentMethods.allowAch) {
					this.portfolioSettings.settings.paymentMethods.allowAch = false;
					this.portfolioSettings.paymentMethods.allowAch = false;
				}

				let min = numeral(accountPortfolio.settings.minimumPaymentAmount).value();
				let max = numeral(accountPortfolio.settings.maximumPaymentAmount).value();

				// If min is defined, and if setting is undefined or if this min value is greater than the previously saved one
				if (
					(accountPortfolio.settings.minimumPaymentAmount !== undefined && accountPortfolio.settings.minimumPaymentAmount !== null)
					&& (this.portfolioSettings.settings.minimumPaymentAmount === undefined || this.portfolioSettings.settings.minimumPaymentAmount < min)
				){
					this.portfolioSettings.settings.minimumPaymentAmount = min;
				}

				// If max is defined, and if setting is undefined or if this max value is less than the previously saved one
				if (
					(accountPortfolio.settings.maximumPaymentAmount !== undefined && accountPortfolio.settings.maximumPaymentAmount !== null)
					&& (this.portfolioSettings.settings.maximumPaymentAmount === undefined || this.portfolioSettings.settings.maximumPaymentAmount > max)
				) {
					this.portfolioSettings.settings.maximumPaymentAmount = max;
				}
			}
		});

		this.globalState.business.applyPortfolioSettingsToPortal(this.portfolioName, true, this.merchantKey);
		this.showAcceptedPaymentMethods(this.portfolioSettings);
		this.model.walletData.portfolioName = this.model.cardData.portfolioName = this.portfolioName;
	}

	disableFormFieldsChanged(newValue, oldValue) {
		if (!newValue && oldValue && this.accounts && this.accounts.length) {
			this.applyPortfolioSettingsToPortal();
		}
	}

	getDigitalWalletIcon(img: string): NodeRequire {
		return require(`../../images/digitalWallets/${img}`);
	}

	// ToDo: In order to sufficeintly test we need to refactor getCustomerPaymentMethods() 
	// to return a paymentMethods array. We should not set the global paymentMethods[] in the resolved promise
	showAcceptedPaymentMethods(portfolio) {
		let creditOnly = portfolio.paymentMethods.acceptsCredit && !portfolio.paymentMethods.acceptsDebit;
		let debitOnly = !portfolio.paymentMethods.acceptsCredit && portfolio.paymentMethods.acceptsDebit;
		let neither = !portfolio.paymentMethods.acceptsCredit && !portfolio.paymentMethods.acceptsDebit;
		// At this time, digital wallets do not support Autopay
		let enableApplePay = this.globalState.features.enableApplePay
			&& this.globalState.business.portalSettings.businessSettings.enableApplePay
			&& this.globalState.business.portalSettings.paymentMethods.allowApplePay
			&& this.paymentPlanType !== PaymentPlanType.Autopay;

		let cardLabel = debitOnly ? this.i18n.i18next.t('debitCard', 'Debit Card') : creditOnly ? this.i18n.i18next.t('creditCard', 'Credit Card') : this.i18n.i18next.t('creditOrDebitCard', 'Credit/Debit Card');
		if (!this.paymentMethods) {
			this.paymentMethods = [];

			let customerIds: Array<string> = [this.globalState.customer.id];
			this.globalState.associatedCustomers.forEach(x => {
				customerIds.push(x.id);
			});

			customerIds.forEach(customerId => {
				this.getCustomerPaymentMethods(customerId).then(
					(result) => {
						this.paymentMethods = [];
						if (this.isWalletEnabled && !this.isEzpay && this.model.walletData.savedPaymentMethods.length) {
							this.paymentMethods.push({ name: this.i18n.i18next.t('wallet', 'Wallet'), vmName: './wallet-form/wallet-form', vm: this.model.walletData, isWallet: true });
						}
						if (this.globalState.business.portalSettings.paymentMethods.allowCreditCard) {
							if (!neither) {
								this.paymentMethods.push({ name: cardLabel, vmName: './card-form/card-form', vm: this.model.cardData, isCard: true });
							}
						}
						if (this.globalState.business.portalSettings.paymentMethods.allowAch) {
							this.paymentMethods.push({ name: this.i18n.i18next.t('eCheck', 'eCheck'), vmName: './check-form/check-form', vm: this.model.checkData, isCheck: true });
						}
						if (enableApplePay && !neither) {
							this.paymentMethods.push({ name: this.i18n.i18next.t('applePay', 'Apple Pay'), vmName: null, vm: this.model.applePaySettings, isDigitalWallet: true, img: 'apple_pay.svg' });
						}
						this.setDefaultPaymentMethod(cardLabel);
						if (!this.model.paymentMethod.isDigitalWallet) {
							this.getFeeInformation();
						}
					});
			});
		}
	}

	setDefaultPaymentMethod(cardLabel: string): void {
		if (!this.paymentMethods || this.paymentMethods.length === 0) {
			this.paymentMethods = [];
			// if paymentMethods is empty, we just default to Credit/Debit Card form.
			this.paymentMethods.push({ name: cardLabel, vmName: './card-form/card-form', vm: this.model.cardData, isCard: true });
		}
		if (this.globalState.business.portalSettings.paymentMethods.allowAch
			&& this.paymentMethods.filter(x => x.isCheck === true).length > 0
			&& this.globalState.business.portalSettings.setDefaultPaymentMethodACH
			) {
				this.model.paymentMethod = this.paymentMethods.find(x => x.isCheck === true);
		}else {
			this.model.paymentMethod = this.paymentMethods[0];
		}
	}

	getCustomerPaymentMethods(customerId: string): Promise<any> {
		if (!customerId) {
			return new Promise((res, rej) => { res({}); });
		}
		return this.payrazrService.getCustomerPaymentMethodsByCustomerId(customerId).then(response => {
			if (response.isSuccessful) {
				let bankAccounts = [];
				let creditCards = [];

				// hideAchPaymentMethods gets set on the attached method of the payment form.
				if (this.globalState.business.portalSettings.paymentMethods.allowAch && !this.model.walletData.hideAchPaymentMethods) {
					bankAccounts = response.entity.achPaymentMethods;
				}

				if (this.globalState.business.portalSettings.paymentMethods.allowCreditCard) {
					creditCards = response.entity.creditCardPaymentMethods;
				}

				let savedPaymentMethods = [...bankAccounts, ...creditCards];

				if (!this.model.walletData.selectedPaymentMethod || (!this.model.walletData.selectedPaymentMethod.cardInfoKey && !this.model.walletData.selectedPaymentMethod.checkInfoKey)) {
					this.model.walletData.selectedPaymentMethod = savedPaymentMethods.find(function(e) {
						return e.isPrimaryPaymentMethod === true;
					});
					// if payment method key exists try to match it to a saved payment method
					if (this.paymentMethodKey) {
						let key = this.paymentMethodKey;
						let selectedPaymentMethod = savedPaymentMethods.find(function(e) {
							return e.checkInfoKey === key || e.cardInfoKey === key;
						});
						// if there is no matching payment method, default to the primary method
						this.model.walletData.selectedPaymentMethod = selectedPaymentMethod
							? selectedPaymentMethod
							: this.model.walletData.selectedPaymentMethod;
					}
				}

				for (let element in savedPaymentMethods) {
					if (savedPaymentMethods[element].cardInfoKey) {
						savedPaymentMethods[element].nameLabel = this.i18n.i18next.t('cardHolderName', 'Card Holder Name');
					}
					else {
						savedPaymentMethods[element].nameLabel = this.i18n.i18next.t('nameOnAccount', 'Name on Account');
					}
				}

				this.model.walletData.savedPaymentMethods = this.model.walletData.savedPaymentMethods.concat(savedPaymentMethods);
				this.model.walletData.cvvLabel = this.i18n.i18next.t('cardSecurityCode', 'Card Security Code');
			} else {
				console.info(response.message);
			}
		}
		).catch(err => {
			console.error('Failed to retrieve customer payment methods');
		});
	}

	inspectCustomFields(customFields) {
		let result = {};
		for (let ii = 0; ii < customFields.length; ++ii) {
			if (customFields[ii].visible && customFields[ii].required && !customFields[ii].value) {
				let fieldName = customFields[ii].label || `Custom field ${ii + 1}`;
				result[fieldName] = `${fieldName} is required`;
			}
		}
		return result;
	}

	balanceChanged() {
		try {
			if (this.balance > 0) {
				if (this.initializeAmountAsBalance) {
					this.model.amount = numeral(this.balance).format('0.00');
				}

				if (!this.model.maxPayment || this.model.maxPayment > this.balance) {
					this.model.maxPayment = this.balance;
					this.maxIsBalanceDue = true;
				}
				this.getFeeInformation();
			}
		}
		catch (error) {
			this.model.amount = 0;
		}
	}

	customerChanged() {
		if (this.model.accountName === '' && this.customer) {
			this.model.accountName = this.customer.name;
		}

		if (this.model.accountZip === '' && this.customer && this.customer.primaryAddress) {
			this.model.accountZip = this.customer.primaryAddress.zip;
		}
	}

	amountChanged(newValue, oldValue) {// bindable at top of this file
		this.model.amount = this.amount;
		this.paymentAmountChanged();
	}

	paymentAmountChanged() {// event on html
		try {
			if (this.model.amount > 0) {
				this.model.amount = numeral(this.model.amount).format('0.00');
				if (!this.model.maxPayment || (this.balance > 0 && this.model.maxPayment > this.balance)) {
					this.model.maxPayment = this.balance;
					this.maxIsBalanceDue = true;
				}
				this.getFeeInformation(); // total will be re-calculated after getting new fee
			}
		}
		catch (error) {
			this.model.amount = 0;
		}
	}

	accountZipChanged() {
		this.getFeeInformation();
	}

	paymentMethodSelected(theEvent, selectedPaymentMethod) {
		this.isDigitalWallet = selectedPaymentMethod?.isDigitalWallet ?? false;
		this.getFeeInformation();
	}

	getFeeInformation() {
		if (this.model.paymentMethod) {
			// default to card if card is selected or no payment method selected
			let isCheck = true;
			if (this.model.paymentMethod.isCard) {
				isCheck = false;
				this.cardNumber = this.model.cardData.cardNumber;
				this.cardInfoKey = null;
			} else if (this.model.paymentMethod.isWallet && this.model.paymentMethod.vm.getSelectedPaymentMethod) {
				isCheck = this.model.paymentMethod.vm.getSelectedPaymentMethod.checkInfoKey;
				this.cardNumber = null;
				this.cardInfoKey = isCheck ? null : this.model.paymentMethod.vm.getSelectedPaymentMethod.cardInfoKey;
			} else if (this.model.paymentMethod.isDigitalWallet) {
				// digital wallet fees are calcualted after payment method selected in third party modal
				this.hideFeeAndTotal = true;
				this.model.convenienceFee = 0;
				this.fee = 0;
				this.feeValue = 0;
				this.feeValues = [];
				this.calculateTotalAmount();
				return;
			}

			let customerAccountKeys = [];
			this.accounts.forEach(account => {
				if (account.customerAccountKey) {
					customerAccountKeys.push(account.customerAccountKey);
				}
			});
			this.payrazrService
				.calculateFee(!isCheck, this.portfolioName, this.model.getZipCode, this.model.amount, this.cardNumber,
					this.cardInfoKey, customerAccountKeys.length ? customerAccountKeys : null, this.paymentPlanType, this.merchantKey)
				.then(result => {
					if (result.isSuccessful) {
						this.fee = result.entity.fee; // for backwards compatibility
						this.model.convenienceFee = numeral(this.fee).format('0.00'); // for backwards compatibility
						this.feeValue = result.entity.feeValue;
						this.feeCalculationType = result.entity.feeCalculationType;
						this.feeValues = result.entity.feeValues;

						this.model.feeCalculationType = this.feeCalculationType;
						if (this.feeCalculationType === FeeCalculationType.Percentage) {
							this.model.feeValue = numeral(this.fee).format('0.0000');
						} else {
							this.model.feeValue = numeral(this.fee).format('0.00');
						}

						if (this.paymentPlanType === PaymentPlanType.Autopay) {
							this.isTieredFees = this.getIsTieredFees(this.feeValues);
							this.formattedConvenienceFee = this.getFormattedConvenienceFee(this.feeValues, this.isFeeOverridden, this.isTieredFees);
						}

						this.model.convenienceFeeDisclosure = he.decode(result.entity.feeDisclosure);

						this.calculateTotalAmount();
					}
				})
				.catch(error => { console.error('Error retrieving fee information'); });
		}
	}

	getIsTieredFees(feeCalculationValues: FeeCalculationValue[]): boolean {
		return !this.isFeeOverridden && feeCalculationValues.some((fv) => fv.tierLimitKey > 1);
	}

	getFormattedConvenienceFee(feeCalculationValues: FeeCalculationValue[], feeIsOverridden: boolean, isTieredFees: boolean): string {
		if (!feeIsOverridden && feeCalculationValues.every(fv => fv.tierLimitKey === 1)) {
			let tier1FeeValue = feeCalculationValues.find(fv => fv.tierLimitKey === 1);
			return tier1FeeValue.feeCalculationType === FeeCalculationType.Percentage
				? `${numeral(tier1FeeValue.feeValue).format('0.[0000]')}%`
				: numeral(tier1FeeValue.feeValue).format('($0,0.00)');
		}
		// if the fee is overridden or tiered, the formatted fee will already have correct format
		return this.formattedConvenienceFee;
	}

	calculateTotalAmount() {
		// if fee calculation done by percentage, it could either spit out calculated $ amount
		// or the % amount. The value needed depends on the context. For now, retain the behavior
		// of using the calculated $ amount, but this may need to change in the future.
		this.totalAmount = numeral(this.model.amount).value() + numeral(this.model.convenienceFee).value();
		this.model.totalAmount = numeral(this.totalAmount).format('0.00');
	}

	submit() {
		this.getSubmitEventData()
			.then(data => {
				this.dispatchCustomEvent('payment-method-entered', data);
			});
	}

	doBackButton() {
		this.dispatchCustomEvent('payment-method-back');
	}

	// this method is for portal edit transaction button. When a page use view-model, this method could be used to clear up payment method PCI data.
	clearPciData() {
			this.model.cardData.cardNumber = '';
			this.model.cardData.cvv = '';
			this.model.checkData.accountNumber = '';
	}

	doResetButton() {
		this.model.amount = 0;
		if (this.resetAccountInfo) {
			this.model.accountName = '';
			this.model.accountZip = '';
			this.model.billingAddress = '';
			this.model.billingEmail = '';
			this.model.billingPhone = '';
		}
		this.model.description = '';

		this.model.cardData.cardNumber = '';
		this.model.cardData.cvv = '';
		let newDate = new Date();
		this.model.cardData.expMonth = (newDate.getMonth() + 1).toString();
		this.model.cardData.expYear = newDate.getFullYear().toString();
		this.model.cardData.customFields.forEach((item, index, list) => {
			item.value = '';
		});

		this.model.checkData.accountNumber = '';
		this.model.checkData.transitNumber = '';
		this.model.checkData.accountType = 'checking';
		this.model.checkData.checkNumber = '';
		this.model.checkData.customFields.forEach((item, index, list) => {
			item.value = '';
		});

		this.model.date = this.date;

		this.dispatchCustomEvent('payment-method-reset');
	}

	dispatchCustomEvent(eventName: any, eventData?: any) {
		let evt = DOM.createCustomEvent(eventName, { bubbles: true, cancelable: true, detail: eventData });

		this.element.dispatchEvent(evt);
	}

	isEzpayForm() {
		return this.router.currentInstruction.config.name === 'ezpay';
	}

	@computedFrom('globalState.business.portalSettings')
	get isWalletEnabled() {
		try {
			return 'consumerWalletEnabled' in this.globalState.business.portalSettings ? this.globalState.business.portalSettings.consumerWalletEnabled : true;
		}
		catch (err) {
			console.error('Failed to enable wallet.');
			return false;
		}
	}

	@computedFrom('model.getAccountName')
	get needThirdPartyAck() {
		let needThirdPartyAck = !!this.customer && (this.model.getAccountName !== this.customer.name) &&
			(this.globalState.business.verticalMarket === 1); // only show for ARM vertical

		if (!needThirdPartyAck) {
			this.model.thirdPartyAck = false;
		}

		return this.customer && needThirdPartyAck;
	}

	getSubmitEventData() {
		let validator = new Validator(this.i18n);
		if (this.maxIsBalanceDue) {
			validator.maxIsBalanceDue = this.maxIsBalanceDue;
		}

		if (this.model) {
			this.model.disableAmountValidation = this.disableAmountValidation;
		}

		return validator.validate(this.model)
			.then(async hasErrors => {
				let data: any = {};
				if (hasErrors) {
					data.errors = validator.errors;
				} else {
					this.model.description = HtmlHelper.HtmlBreak(this.model.description);
					data.name = this.model.accountName;
					data.addressLine1 = this.model.billingAddress;
					data.billingCity = this.model.billingCity;
					data.billingState = this.model.billingState;
					data.zipCode = this.model.accountZip;
					data.billingPhone = this.model.billingPhone;
					data.billingEmail = this.model.billingEmail;
					data.isEmailOptedIn = this.model.isEmailOptedIn;
					data.amount = this.model.amount;
					data.transactionDescription = this.model.description;
					data.isThirdPartyPayment = this.model.thirdPartyAck;
					data.addPaymentMethodToWalletIsSelected = this.model.addPaymentMethodToWalletIsSelected && !this.model.paymentMethod.isWallet;
					data.date = this.model.date;

					if (this.model.paymentMethod.isWallet) {
						data.isWallet = true;
						if (this.model.paymentMethod.vm.getSelectedPaymentMethod.cardInfoKey) {
							data.isCardPayment = true;
							data.isCheckPayment = false;
							data.card = this.model.paymentMethod.vm;
						}
						else {
							data.isCardPayment = false;
							data.isCheckPayment = true;
							data.check = this.model.paymentMethod.vm;
						}
					}
					else if (this.model.paymentMethod.isCard) {
						data.isCardPayment = true;
						data.isCheckPayment = false;
						data.card = this.model.cardData;
						data.description = this.model.cardData.description;
					}
					else if (this.model.paymentMethod.isCheck) {
						data.isCardPayment = false;
						data.isCheckPayment = true;
						data.check = this.model.checkData;
						data.description = this.model.checkData.description;
						data.paymentMethod = 'Check';
					}
					else if (this.model.paymentMethod.isDigitalWallet) {
						data.isCardPayment = false;
						data.isCheckPayment = false;
						data.isDigitalWallet = true;
						data.digitalWalletSettings = this.model.paymentMethod.vm;
						data.digitalWalletName = this.model.paymentMethod.vm.getDigitalWalletName();
					}

					// begin error checks
					data.errors = [];

					if (this.model.paymentMethod.isCheck) {
						if (data.check) {
							let checkPaymentMethod = new CheckPaymentMethod();
							checkPaymentMethod.aba = data.check.transitNumber;
							checkPaymentMethod.accountNumber = data.check.accountNumber;

							let customerKey = this.customer === null ? 0 : this.customer.customerKey;

							let isCheckAccepted = await this.hpfPaymentMethodService.verifyCheckAccepted(
								customerKey,
								checkPaymentMethod,
								'C')
								.then(response => response.isSuccessful);

							if (!isCheckAccepted) {
								data.errors.push(this.i18n.i18next.t(
									'verifyCheckAcceptedConsumerError',
									'Our payment processor, REPAY, cannot validate your bank account information. Please use a different form of payment to complete your transaction.'));
							}
						}
					}

					if (this.model.paymentMethod.isDigitalWallet && data.digitalWalletSettings) {
						// apple pay
						if (data.digitalWalletSettings.getDigitalWalletType() === DigitalWalletType.ApplePay) {
							if (!window.ApplePaySession || !window.ApplePaySession.canMakePayments()) {
								data.errors.push(this.i18n.i18next.t(
									'applePayBrowserIncompatible',
									'Browser is incompatible with Apple Pay'));
							}
						}
					}
				}

				return data;
			});
	}

	@computedFrom('model.paymentMethod')
	get addPaymentMethodToWalletIsVisible() {
		try {
			let visible = this.showAddPaymentMethodToWallet
				&& ('consumerWalletEnabled' in this.globalState.business.portalSettings ? this.globalState.business.portalSettings.consumerWalletEnabled : true)
				&& (!!this.model.paymentMethod.isCard || !!this.model.paymentMethod.isCheck)
				&& !this.isEzpay;

			// This resets the value of the checkbox when visibility changes.
			if (!visible) {
				this.model.addPaymentMethodToWalletIsSelected = false;
			}

			return visible;
		}
		catch (err) {
			return false;
		}
	}

	checkFirstColVisibility() {
		// Check visibility of all first column elements
		// If none are visible, hide the whole column shifting others
		if (
			!this.showDate &&
			!this.showAmount &&
			this.hideFeeAndTotal &&
			!this.model.descriptionSettings.visible &&
			!this.model.billingPhoneSettings.visible &&
			!this.model.billingEmailSettings.visible
		) {
			this.showFirstCol = false;
		}
	}

	attached() {
		if (this.forceEmailRequired) {
			this.model.billingEmailSettings.required = this.forceEmailRequired;
			this.model.billingEmailSettings.visible = this.forceEmailRequired;
		}

		this.model.merchantKey = this.merchantKey ?? 0;
		this.applyPortfolioSettingsToPortal();

		if (this.noMethodSelected) {
			this.model.paymentMethod = null;
		}
		if (this.paymentMethods.length) {
			if (this.notShowEcheck) {
				this.paymentMethods = this.paymentMethods.filter(item => !item.isCheck);
			}
		}

		this.model.walletData.hideAchPaymentMethods = this.notShowEcheck;

		this.model.walletData.showCvv = this.model.cardData.showCvv = this.globalState.business.portalSettings.fields.cvv.visible && this.showCvv !== false;
		this.model.checkData.showCheckNumber = this.showCheckNumber !== false;
		this.model.checkData.isRehabPaymentPlanForm = this.isRehabPaymentPlanForm;
		this.model.cardData.isRehabForm = this.isRehabPaymentPlanForm;

		this.model.cardData.customFields.forEach((item, index, list) => {
			item.value = '';
		});
		this.model.checkData.customFields.forEach((item, index, list) => {
			item.value = '';
		});

		// apple pay
		if (this.globalState.features.enableApplePay) {
			this.model.applePaySettings.acceptsCredit = this.allowCardType('credit', this.portfolioSettings);
			this.model.applePaySettings.acceptsDebit = this.allowCardType('debit', this.portfolioSettings);
			this.model.applePaySettings.supportedNetworks = this.getAcceptedCards(this.portfolioSettings);
		}

		// The balance is bound to the component in most cases, but when it isn't, pull the value from the selected account.
		if (!this.balance && this.accountsBalance) {
			this.balance = this.accountsBalance;
		}

		// These min/max values don't appear to be tied to the portfolio, with the shopping cart changes we may have issues with this.
		let minimum = this.accountMinPayment && this.enforceMinPayment ? this.accountMinPayment : (this.globalState.business.portalSettings.minimumPaymentAmount || 0.01);
		let maximum = Math.min(this.globalState.business.portalSettings.maximumPaymentAmount || Number.MAX_VALUE, this.balance || Number.MAX_VALUE);
		if (maximum === Number.MAX_VALUE) {
			maximum = undefined;
		}

		let maxIsBalanceDue = maximum === this.balance;
		this.model.setPaymentLimits(minimum, maximum, maxIsBalanceDue);

		if (this.customer === null) {
			this.model.wallet = null;
			this.model.billingAddress = '';
			this.model.billingPhone = '';
			this.model.billingEmail = '';
			this.model.isEmailOptedIn = false;
			this.model.amount = 0;
			this.model.accountName = '';
			this.model.accountZip = '';
		} else {
			if (this.customer.paymentMethods) {
				this.model.wallet = this.customer.paymentMethods.filter(item => {
					return (item.type === 'Card' && this.globalState.business.portalSettings.paymentMethods.allowCreditCard) ||
						(item.type === 'ACH' && this.globalState.business.portalSettings.paymentMethods.allowAch);
				});

				if (this.model.wallet.length === 0) {
					this.model.wallet = null;
				}
			} else {
				this.model.wallet = null;
			}
			this.model.billingAddress = this.customer.primaryAddress.line1;
			this.model.billingPhone = this.customer.homePhone;
			this.model.billingEmail = this.customer.email;
			this.model.isEmailOptedIn = this.customer.notificationSettings.isEmailOptedIn;
			this.model.billingCity = this.customer.primaryAddress.city;
			this.model.billingState = this.customer.primaryAddress.state;
			this.model.amount = this.amount;
			this.model.accountName = this.customer.name;
			this.model.accountZip = this.customer.primaryAddress.zip;
		}

		if (this.paymentInformation) {
			this.model.accountName = this.paymentInformation.name;
			this.model.billingAddress = this.paymentInformation.addressLine1;
			this.model.billingCity = this.paymentInformation.billingCity;
			this.model.billingState = this.paymentInformation.billingState;
			this.model.accountZip = this.paymentInformation.zipCode;
			this.model.billingPhone = this.paymentInformation.billingPhone;
			this.model.billingEmail = this.paymentInformation.billingEmail;
			this.model.isEmailOptedIn = this.paymentInformation.isEmailOptedIn;
			this.model.amount = this.paymentInformation.amount;
			this.model.description = this.paymentInformation.transactionDescription;
			if (this.paymentInformation.card) {
				this.paymentInformation.isCardPayment = true;
				this.paymentInformation.isCheckPayment = false;
				this.model.paymentMethod = this.paymentMethods.find(item => item.isCard);
				this.model.cardData = this.paymentInformation.card;
				this.model.paymentMethod.vm = this.model.cardData;
			} else if (this.paymentInformation.check) {
				this.paymentInformation.isCardPayment = false;
				this.paymentInformation.isCheckPayment = true;
				this.model.paymentMethod = this.paymentMethods.find(item => !item.isCard);
				this.model.checkData = this.paymentInformation.check;
				this.model.paymentMethod.vm = this.model.checkData;
			} else if (this.paymentInformation.wallet) {
				this.model.walletSelect = this.paymentInformation.wallet;
			}
		}

		if (this.globalState.business.portalSettings.ezpay.accountNumberHandling.toLowerCase() !== 'lookup') {
			this.applyQueryStringParams();
		}

		this.paymentAmountSettings = this.globalState.business.portalSettings.fields.paymentAmount;
		if (!!this.paymentAmountSettings && !this.paymentAmountSettings.label) { this.paymentAmountSettings.label = this.i18n.i18next.t('paymentAmount', 'Payment Amount'); }

		this.dateSettings = this.globalState.business.portalSettings.fields.paymentDate;
		if (!!this.dateSettings && !this.dateSettings.label) { this.dateSettings.label = this.i18n.i18next.t('paymentDate', 'Payment Date'); }

		this.zipcodeSettings = this.globalState.business.portalSettings.fields.zipcode;
		if (!!this.zipcodeSettings && !this.zipcodeSettings.label) { this.zipcodeSettings.label = this.i18n.i18next.t('billingZipCode', 'Billing Zip Code'); }

		this.model.date = this.date ? moment(this.date).format('MM/DD/YYYY') : moment.tz(this.globalState.business.businessTimezone).format('MM/DD/YYYY');

		// for ezpay (both validated and unvalidated), the first and only fee calculation call (when the page loads) happens before the
		// attached() method has completed and before the payment methods html has been rendered -- therefore there isn't enough information
		// available to calculate the fee (e.g. payment method type). So call the fee calculation method here for ezpay after the
		// model has finished loading.
		// Non - ezpay application flows already have the necessary information loaded in the page.
		// but still need an additional call if clicking 'back' button since this component page will be reloaded and this.model will be reset in the case
		// TODO: calculateFee() has heavy usage in our system since multiple events trigger to recalculate fee, like zipcode, payment method type, payment amount and card number
		// we might think a way to reduce db call later
		this.getFeeInformation();
		this.checkFirstColVisibility();

		this.displaySelectedWalletToCustomer = this.globalState.isConsumerPortalGuarantorBillingEnabled
			&& this.displaySelectedWalletToCustomer;

		this.subscribeToEvents();
	}

	detached() {
		if (this.ezPayMerchantSelectedChanged) {
			this.ezPayMerchantSelectedChanged.dispose();
		}
	}

	subscribeToEvents() {
		if (this.globalState.features.consumerPortalMultiZidEzPay) {
			this.ezPayMerchantSelectedChanged = this.eventAggregator.subscribe('ezPayMerchantSelectedChangedEvent', (merchantKey) => {
				this.merchantKey = merchantKey;
				this.getFeeInformation();
			});
		}
	}

	allowCardType(cardType: string, portfolioSettings: any) {
		if (!cardType || !portfolioSettings || !portfolioSettings.paymentMethods) {
			return false;
		}

		switch (cardType.toLowerCase()) {
			case 'credit':
				return portfolioSettings.paymentMethods.acceptsCredit;
			case 'debit':
				return portfolioSettings.paymentMethods.acceptsDebit;
			default:
				return false;
		}
	}

	getAcceptedCards(portfolioSettings: any): string[] {
		let cardNames: string[] = [];
		let acceptedCards: any;
		if (this.isEzpay && !!portfolioSettings.ezPayMerchants && this.merchantKey > 0) {
			acceptedCards = portfolioSettings.ezPayMerchants.find(m => m.merchantKey == this.merchantKey).merchantSettings.cardsAccepted;
		} else {
			acceptedCards = portfolioSettings.merchantSettings.cardsAccepted;
		}
		const acceptedCardKeys: string[] = Object.keys(acceptedCards);

		acceptedCardKeys.forEach((key) => {
			if (acceptedCards[key]) {
				cardNames.push(key);
			}
		});

		return cardNames;
	}

	showConvenienceFee(hideFeeTotal: boolean, convenienceFee: number) {
		return !hideFeeTotal && convenienceFee;
	}

	get accountsBalance() {
		if (!this.cachedCombinedAccountsBalance) {
			this.cachedCombinedAccountsBalance = 0;
			this.accounts.forEach(account => {
				this.cachedCombinedAccountsBalance += account.balance;
			});
		}

		return this.cachedCombinedAccountsBalance;
	}

	@computedFrom('states')
	get stateIds(): string[] {
		return this.states.map(s => s.id);
	}

	@computedFrom('states')
	get stateDescs(): string[] {
		return this.states.map(s => s.name);
	}

	@computedFrom('model')
	get defaultState() {
		return this.model.billingState;
	}

	// Both this and avalableCustomerDescs are called multiple times by
	// the view for unsure reasons; if there's a notable performance lag
	// in the future when switching customers/patients, try debugging here.
	@computedFrom('globalState')
	get availableCustomers(): Customer[] {
		return [this.globalState.customer].concat(this.globalState.associatedCustomers);
	}

	@computedFrom('globalState')
	get availableCustomerDescs(): string[] {
		return [this.globalState.customer].concat(this.globalState.associatedCustomers).map(c => c.name);
	}
}

export class Model {
	cardData: any;
	checkData: any;
	walletData: any;
	applePaySettings: ApplePaySettings;
	paymentMethod: any;
	thirdPartyAck: any;
	amount: any;
	convenienceFee: any;
	feeValue: number;
	feeCalculationType: FeeCalculationType;
	totalAmount: any;
	serviceFeeAck: any;
	businessLegalName: any;
	csPhone: any;
	convenienceFeeDisclosure: any;
	addPaymentMethodToWalletIsSelected: any;
	description: any;
	billingAddress: any;
	billingCity: any;
	billingState: any;
	billingPhone: any;
	billingEmail: any;
	isEmailOptedIn: boolean;
	accountName: any;
	accountZip: any;
	billingAddressSettings: any;
	billingCitySettings: any;
	descriptionRequiredStyle: any;
	billingStateSettings: any;
	billingPhoneSettings: any;
	billingEmailSettings: any;
	descriptionSettings: any;
	billAddressRequiredStyle: any;
	billCityRequiredStyle: any;
	billStateRequiredStyle: any;
	billPhoneRequiredStyle: any;
	billEmailRequiredStyle: any;
	minPayment: any;
	maxPayment: any;
	maxIsBalanceDue: any;
	disableAmountValidation: any = false;
	wallet: any;
	walletSelect: any;
	date: any;
	merchantKey: any;

	constructor(globalState, payrazrService, private i18n: I18N) {
		this.cardData = new CardFormData(payrazrService, this.i18n);
		this.cardData.cvvLabel = globalState.business.portalSettings.fields.cvv.label;
		this.cardData.nameLabel = this.i18n.i18next.t('cardHolderName', 'Card Holder Name');
		this.cardData.placeholder = this.i18n.i18next.t('enterCardHoldersName', 'Enter the card holder\'s name.');
		this.cardData.customFields = globalState.business.portalSettings.fields.cardCustom;
		this.cardData.cvvRequired = globalState.business.portalSettings.fields.cvv.required;

		this.checkData = new CheckFormData(this.i18n, globalState);
		this.checkData.nameLabel = this.i18n.i18next.t('nameOnAccount', 'Name on Account');
		this.checkData.placeholder = this.i18n.i18next.t('enterAccountHoldersName', 'Enter the account holder\'s name.');
		this.checkData.customFields = globalState.business.portalSettings.fields.checkCustom;

		this.walletData = new WalletForm(globalState, payrazrService, this.i18n);
		this.walletData.cvvLabel = globalState.business.portalSettings.fields.cvv.label;
		this.walletData.cvvRequired = globalState.business.portalSettings.fields.cvv.required;

		this.walletData.cardCustomFields = globalState.business.portalSettings.fields.cardCustom;
		this.walletData.checkCustomFields = globalState.business.portalSettings.fields.checkCustom;

		this.applePaySettings = new ApplePaySettings(this.i18n, globalState);

		this.paymentMethod = null;

		this.thirdPartyAck = false;
		this.amount = 0;
		this.convenienceFee = 0;
		this.feeValue = 0;
		this.feeCalculationType = FeeCalculationType.NotSet;
		this.totalAmount = 0;
		this.serviceFeeAck = false;
		this.businessLegalName = globalState.business.legalName;
		this.csPhone = globalState.business.portalSettings.csPhone;
		this.convenienceFeeDisclosure = '';

		this.addPaymentMethodToWalletIsSelected = false;

		this.description = '';
		this.billingAddress = '';
		this.billingCity = '';
		this.billingState = '';
		this.billingPhone = '';
		this.billingEmail = '';
		this.isEmailOptedIn = false;
		this.accountName = '';
		this.accountZip = '';
		this.merchantKey = 0;

		this.billingAddressSettings = globalState.business.portalSettings.fields.billingAddress;
		if (!this.billingAddressSettings.label) { this.billingAddressSettings.label = this.i18n.i18next.t('billingAddress', 'Billing Address'); }
		this.billingCitySettings = globalState.business.portalSettings.fields.billingCity;
		if (!!this.billingCitySettings && !this.billingCitySettings.label) { this.billingCitySettings.label = this.i18n.i18next.t('billingCity', 'Billing City'); }
		this.billingStateSettings = globalState.business.portalSettings.fields.billingState;
		if (!!this.billingStateSettings && !this.billingStateSettings.label) { this.billingStateSettings.label = this.i18n.i18next.t('billingState', 'Billing State'); }
		this.billingPhoneSettings = globalState.business.portalSettings.fields.billingPhone;
		if (!this.billingPhoneSettings.label) { this.billingPhoneSettings.label = this.i18n.i18next.t('billingPhoneNumber', 'Billing Phone Number'); }
		this.billingEmailSettings = globalState.business.portalSettings.fields.billingEmail;
		if (!this.billingEmailSettings.label) { this.billingEmailSettings.label = this.i18n.i18next.t('email', 'Email'); }
		this.descriptionSettings = globalState.business.portalSettings.fields.transactionDescription;
		if (!this.descriptionSettings.label) { this.descriptionSettings.label = this.i18n.i18next.t('description', 'Description'); }

		this.descriptionRequiredStyle = this.descriptionSettings.required ? 'required' : 'optional';
		this.billAddressRequiredStyle = this.billingAddressSettings.required ? 'required' : 'optional';
		this.billCityRequiredStyle = (!!this.billingCitySettings && this.billingCitySettings.required) ? 'required' : 'optional';
		this.billStateRequiredStyle = (!!this.billingStateSettings && this.billingStateSettings.required) ? 'required' : 'optional';
		this.billPhoneRequiredStyle = this.billingPhoneSettings.required ? 'required' : 'optional';
		this.billEmailRequiredStyle = this.billingEmailSettings.required ? 'required' : 'optional';		
	}

	@computedFrom('paymentMethod')
	get isWalletSelected() {
		try {
			return (this.paymentMethod.isWallet !== undefined && this.paymentMethod.isWallet);
		}
		catch (err) {
			return false;
		}
	}

	// TODO: CR - Rename the property to noun
	@computedFrom('accountName', 'paymentMethod', 'walletData.getSelectedPaymentMethod.checkInfoKey', 'walletData.getSelectedPaymentMethod.cardInfoKey')
	get getAccountName() {
		try {
			if (this.isWalletSelected && (this.walletData.savedPaymentMethods.length > 0) && this.walletData.getSelectedPaymentMethod.name) {
				this.accountName = this.walletData.getSelectedPaymentMethod.name;
			}

			return this.accountName;
		}
		catch (err) {
			return this.accountName;
		}
	}

	// TODO: CR - Rename the property to noun
	set getAccountName(param) {
		this.accountName = param;
	}

	// TODO: CR - Rename the property to noun
	@computedFrom('accountZip', 'paymentMethod', 'walletData.getSelectedPaymentMethod.checkInfoKey', 'walletData.getSelectedPaymentMethod.cardInfoKey')
	get getZipCode() {
		try {
			if (this.isWalletSelected && (this.walletData.savedPaymentMethods.length > 0) && this.walletData.getSelectedPaymentMethod.zipCode) {
				this.accountZip = this.walletData.getSelectedPaymentMethod.zipCode;
			}

			return this.accountZip;
		}
		catch (err) {
			return this.accountZip;
		}
	}

	// TODO: CR - Rename the property to noun
	set getZipCode(param) {
		this.accountZip = param;
	}

	@computedFrom('paymentMethod')
	get paymentViewModelName() {
		if (!this.paymentMethod) {
			return null;
		}
		return this.paymentMethod.vmName;
	}

	@computedFrom('paymentMethod')
	get paymentViewModelData() {
		if (!this.paymentMethod) {
			return null;
		}
		if (this.merchantKey && this.merchantKey > 0) {
			this.paymentMethod.vm.merchantKey = this.merchantKey;
		}

		return this.paymentMethod.vm;
	}

	setPaymentLimits(minimum, maximum, maxIsBalanceDue) {
		this.minPayment = minimum;
		this.maxPayment = maximum;
		this.maxIsBalanceDue = maxIsBalanceDue;
	}
}

class Validator {
	errors: any;
	maxIsBalanceDue: any; // portal refactor note: had to add this here, even though it's not used in the class -- some external code was referencing/injecting it?

	constructor(private i18n: I18N) {
		this.errors = [];
	}

	validate(model: Model) {
		if (!model.disableAmountValidation && !ValidationHelpers.isNumeric(model.amount)) {
			this.addError(this.i18n.i18next.t('amountMustBeNumber', 'Amount must be a number'));
		} else if (!model.disableAmountValidation) {
			if (parseFloat(model.amount) < parseFloat(model.minPayment)) {
				this.addError(this.i18n.i18next.t('minimumPaymentAmountRequired', 'A minimum payment amount of') + ' ' + numeral(model.minPayment).format('$0,0.00') + ' ' + this.i18n.i18next.t('isRequired', 'is required'));
			} else if (model.maxPayment && parseFloat(model.amount) > parseFloat(model.maxPayment)) {
				this.addError(this.i18n.i18next.t('maximumPaymentAmountAllowed', 'Amount cannot be more than') + ' ' + numeral(model.maxPayment).format('$0,0.00'));
			}
		} else {
			if (parseFloat(model.amount) < 0) {
				this.addError(this.i18n.i18next.t('amountCannotBeLessThan', 'Amount cannot be less than') + ' ' + numeral(0).format('$0,0.00'));
			}
		}

		if (model.billingPhoneSettings.visible && model.billingPhoneSettings.required) {
			if (model.billingPhone.length === 0) {
				this.addError(model.billingPhoneSettings.label + ' ' + this.i18n.i18next.t('isRequired', 'is required'));
			}
		}

		if (model.billingPhoneSettings.visible && model.billingPhoneSettings.required) {
			if (model.billingPhone.length < 10) {
				this.addError(model.billingPhoneSettings.label + ' ' + this.i18n.i18next.t('mustBeTenDigits', 'must be 10 digits'));
			}
		}

		if (model.billingEmailSettings.visible && model.billingEmailSettings.required) {
			if (model.billingEmail.length === 0) {
				this.addError(model.billingEmailSettings.label + ' ' + this.i18n.i18next.t('isRequired', 'is required'));
			}
		}

		if (model.billingEmailSettings.visible && model.billingEmail.length > 0 && !ValidationHelpers.isEmailAddress(model.billingEmail)) {
			this.addError(this.i18n.i18next.t('thisEmailAddressIsNotValid', 'This Email address is not valid.'));
		}

		if (model.descriptionSettings.visible && model.descriptionSettings.required) {
			if (model.description.length === 0) {
				this.addError(model.descriptionSettings.label + ' ' + this.i18n.i18next.t('isRequired', 'is required'));
			}
		}

		// digital wallet validation is handled by each digital wallet component
		if (!model.paymentMethod?.isDigitalWallet) {
			if (model.billingAddressSettings.visible && model.billingAddressSettings.required) {
				if (model.billingAddress.length === 0) {
					this.addError(model.billingAddressSettings.label + ' ' + this.i18n.i18next.t('isRequired', 'is required'));
				}
			}

			if (!!model.billingCitySettings && model.billingCitySettings.visible && model.billingCitySettings.required) {
				if (model.billingCity.length === 0) {
					this.addError(model.billingCitySettings.label + ' ' + this.i18n.i18next.t('isRequired', 'is required'));
				}
			}

			if (!!model.billingStateSettings && model.billingStateSettings.visible && model.billingStateSettings.required) {
				if (model.billingState.length === 0) {
					this.addError(model.billingStateSettings.label + ' ' + this.i18n.i18next.t('isRequired', 'is required'));
				}
			}

			if (model.accountName.length === 0) {
				this.addError(this.i18n.i18next.t('nameRequired', 'Name is required'));
			} else if (model.accountName.length > 25) {
				this.addError(this.i18n.i18next.t('nameCannotBeLongerThanTwentyFiveCharacters', 'Name cannot be longer than 25 characters'));
			}

			const AccountNameHasAtLeastTwoNamesRegex = /[A-Za-z] +[A-Za-z]/;
			if (!AccountNameHasAtLeastTwoNamesRegex.test(model.accountName)) {
				this.addError(this.i18n.i18next.t('enterBothFirstAndLastName', 'Please enter both a first and last name'));
			}

			if (model.accountZip.length === 0) {
				this.addError(this.i18n.i18next.t('zipCodeIsRequired', 'Zip code is required'));
			} else if (!ValidationHelpers.isZipCode(model.accountZip)) {
				this.addError(this.i18n.i18next.t('enterValidFiveOrNineDigitZipCode', 'Please enter a valid 5- or 9-digit zip code'));
			}
		}

		let returnedPromise;

		if (!model.paymentViewModelData) {
			this.addError(this.i18n.i18next.t('selectOnePaymentMethod', 'Please select one payment method'));
		} else {
			returnedPromise = model.paymentViewModelData.validate(this.errors);
		}
		if (!returnedPromise) {
			return Promise.resolve(this.errors.length > 0);
		}

		return returnedPromise
			.then(() => {
				return this.errors.length > 0;
			});
	}

	addError(error) {
		this.errors.push(error);
	}
}
